import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAdmins,
  GetAdminsParams,
  createAdmin,
  updateAdmin,
  deleteAdmin,
} from "@/services/admin";
import type { CreateAdminDto, UpdateAdminDto } from "@/types";

export const ADMINS_QUERY_KEY = "admins";

/**
 * Hook to fetch admins with pagination
 */
export function useAdmins(params?: GetAdminsParams) {
  return useQuery({
    queryKey: [ADMINS_QUERY_KEY, params],
    queryFn: () => getAdmins(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create an admin
 */
export function useCreateAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAdminDto) => createAdmin(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADMINS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update an admin
 */
export function useUpdateAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdminDto }) =>
      updateAdmin(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADMINS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to delete an admin
 */
export function useDeleteAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADMINS_QUERY_KEY] });
    },
  });
}

